<!--
  @component ScopeIO — the Deployments project-scope provider.
  tier:      feature wrapper (above ConsumerShell + the section router so the
             scope selector AND every section read the same scope).
  publishes: SCOPE_IO_KEY → ScopeIOContext → useScopeIO()

  The scope is the spectrum that makes Deployments composable:
      none (empty set) · all · one · many

  The URL is the source of truth: scope is derived from `?p=` on /deployments
  routes (`?p=all` | `?p=a,b,c`), so it's shareable + bookmarkable + back/forward
  friendly. Mutators NAVIGATE (replace) rather than set state — which keeps a
  single, loop-free data flow (URL → scope, never the reverse-write race). A
  pinned default ("Remember this scope") is held in localStorage and used when
  no `?p=` is present.
-->
<script lang="ts" module>
  import { getContext } from "svelte";

  export type Scope =
    | { readonly kind: "all" }
    | { readonly kind: "set"; readonly ids: readonly string[] };

  export interface ScopeIOContext {
    readonly scope: Scope;
    readonly projectIds: readonly string[];
    readonly isAll: boolean;
    readonly count: number;
    readonly singleId: string | null;
    has(id: string): boolean;
    setAll(): void;
    setProjects(ids: readonly string[]): void;
    toggle(id: string): void;
    clear(): void;
    readonly rememberedScope: Scope | null;
    remember(): void;
    forget(): void;
  }

  export const SCOPE_IO_KEY: unique symbol =
    Symbol("consumer.deployments.scope");

  export const SCOPE_STORAGE_KEY = "network:consumer:deployment-scope";

  export function useScopeIO(): ScopeIOContext {
    const ctx = getContext<ScopeIOContext | undefined>(SCOPE_IO_KEY);
    if (!ctx) {
      throw new Error("useScopeIO() called outside of a ScopeIO tree.");
    }
    return ctx;
  }

  /** Serialize a scope for the URL (`all` | comma-joined ids). */
  export function scopeToParam(scope: Scope): string {
    return scope.kind === "all" ? "all" : scope.ids.join(",");
  }

  /** Parse a URL `?p=` value back into a Scope (null when absent/empty). */
  export function scopeFromParam(p: string | null | undefined): Scope | null {
    if (p == null || p === "") return null;
    if (p === "all") return { kind: "all" };
    const ids = p.split(",").map((s) => s.trim()).filter(Boolean);
    return { kind: "set", ids };
  }
</script>

<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { location, querystring, replace } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";

  const { children }: { children?: Snippet } = $props();

  const identity = useIdentityIO();

  function readRemembered(): Scope | null {
    if (typeof localStorage === "undefined") return null;
    try {
      const raw = localStorage.getItem(SCOPE_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Scope;
      if (parsed.kind === "all") return { kind: "all" };
      if (parsed.kind === "set" && Array.isArray(parsed.ids)) {
        return { kind: "set", ids: parsed.ids.filter((x) => typeof x === "string") };
      }
      return null;
    } catch {
      return null;
    }
  }

  let rememberedScope = $state<Scope | null>(readRemembered());

  // ── Scope is derived from the URL (?p=), with the pinned default / "all"
  //    as the fallback when no param is present. ──────────────────────────
  const scope = $derived.by<Scope>(() => {
    const p = new URLSearchParams($querystring ?? "").get("p");
    return scopeFromParam(p) ?? rememberedScope ?? { kind: "all" };
  });

  // Resolved against the live project list — "all" auto-tracks new projects;
  // stale ids in an explicit set drop out.
  const projectIds = $derived.by<readonly string[]>(() => {
    const owned = identity.projects.map((p) => p.id);
    if (scope.kind === "all") return owned;
    const ownedSet = new Set(owned);
    return scope.ids.filter((id) => ownedSet.has(id));
  });
  const isAll = $derived(scope.kind === "all");
  const count = $derived(projectIds.length);
  const singleId = $derived(projectIds.length === 1 ? projectIds[0]! : null);

  // A scope change is a navigation: keep the current section, swap the ?p=.
  function go(next: Scope): void {
    const path = $location.startsWith("/deployments") ? $location : "/deployments";
    replace(`${path}?p=${scopeToParam(next)}`);
  }

  function persistRemembered(): void {
    if (typeof localStorage === "undefined") return;
    try {
      if (rememberedScope) {
        localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(rememberedScope));
      } else {
        localStorage.removeItem(SCOPE_STORAGE_KEY);
      }
    } catch {
      /* swallow — pin degrades to in-memory */
    }
  }

  const ctx: ScopeIOContext = {
    get scope() { return scope; },
    get projectIds() { return projectIds; },
    get isAll() { return isAll; },
    get count() { return count; },
    get singleId() { return singleId; },
    has(id) { return projectIds.includes(id); },
    setAll() { go({ kind: "all" }); },
    setProjects(ids) { go({ kind: "set", ids: [...new Set(ids)] }); },
    toggle(id) {
      const base = scope.kind === "all"
        ? identity.projects.map((p) => p.id)
        : [...scope.ids];
      const i = base.indexOf(id);
      if (i >= 0) base.splice(i, 1);
      else base.push(id);
      go({ kind: "set", ids: base });
    },
    clear() { go({ kind: "set", ids: [] }); },
    get rememberedScope() { return rememberedScope; },
    remember() { rememberedScope = scope; persistRemembered(); },
    forget() { rememberedScope = null; persistRemembered(); },
  } satisfies ScopeIOContext;

  setContext(SCOPE_IO_KEY, ctx);
</script>

{@render children?.()}
